import React from "react";
import { useMenu } from "./MenuContext";
import { localStoreService } from "_services/localStoreService";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  ChevronLeft,
  DriveFileRenameOutline,
  CheckCircle,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const MenuHeader: React.FC = () => {
  const menuContext = useMenu() || ({} as any);
  const theme = useTheme();
  const { toggleThis } = menuContext;

  const localData = localStoreService.retrieveData();
  const userdata = localData?.userdata;
  const isAuthenticated = Boolean(localData && userdata && userdata.email);

  // Derive user display information
  const displayName =
    userdata?.fullname ||
    [userdata?.firstname, userdata?.lastname].filter(Boolean).join(" ") ||
    userdata?.email?.split("@")[0] ||
    "User";

  const userInitial = (
    userdata?.initial ||
    displayName?.[0] ||
    "U"
  ).toUpperCase();

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        color: "common.white",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.15)",
        position: "relative",
      }}
    >
      {/* Brand & Drawer Close Action */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 34,
              height: 34,
              borderRadius: "8px",
              bgcolor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(4px)",
            }}
          >
            <DriveFileRenameOutline sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.5,
                lineHeight: 1.2,
                fontSize: "1rem",
              }}
            >
              Sign & Plus
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.82,
                fontSize: "0.7rem",
                letterSpacing: 0.2,
                display: "block",
              }}
            >
              Digital Signature Platform
            </Typography>
          </Box>
        </Box>

        <Tooltip title="Collapse sidebar" placement="bottom">
          <IconButton
            size="small"
            onClick={() => toggleThis("isMenuOpen", false)}
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              bgcolor: "rgba(255, 255, 255, 0.08)",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
                color: "#fff",
              },
              borderRadius: "8px",
              p: 0.6,
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* User Information Card */}
      {isAuthenticated ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.25,
            borderRadius: "10px",
            bgcolor: "rgba(255, 255, 255, 0.12)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "rgba(255, 255, 255, 0.25)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "1.05rem",
              border: "1.5px solid rgba(255, 255, 255, 0.4)",
            }}
          >
            {userInitial}
          </Avatar>
          <Box sx={{ overflow: "hidden", flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  color: "#fff",
                }}
              >
                {displayName}
              </Typography>
              <CheckCircle sx={{ fontSize: 13, color: "#81c784" }} />
            </Box>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: "rgba(255, 255, 255, 0.78)",
                fontSize: "0.72rem",
                display: "block",
              }}
            >
              {userdata?.email}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            py: 0.5,
            px: 1,
            borderRadius: "8px",
            bgcolor: "rgba(255, 255, 255, 0.08)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "0.74rem" }}
          >
            Welcome to Sign & Plus
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MenuHeader;
